/**
 * Keep WhatsApp Awake — single merged header control.
 *
 * Combines two things that used to be separate pills:
 *   1. The on/off toggle for the keep-awake tab-switching (persisted to the
 *      user doc; synced to every client over MQTT via the users entity update).
 *   2. The live tab-health status (the `3|4` countdown / throttle colors),
 *      which is only shown in debug mode (`bt-debug`).
 *
 * The toggle Switch is one click from the bar. Hovering the status or the "?"
 * opens the explainer (HeaderHint) — no click needed — with "Learn more" → the
 * /keep-whatsapp-awake page in a new tab.
 *
 * Why Chrome needs this: it freezes background tabs to save power, which stalls
 * WhatsApp Web's socket; we briefly focus the tab to wake it. Users who keep
 * WhatsApp in front don't need it and can switch it off here.
 */
import React, { useEffect, useState } from 'react';
import { Zap, ZapOff, CheckCircle, AlertTriangle, ExternalLink } from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import { HeaderHint } from '@common/components/header-status/header-hint';
import { cn } from '@/lib/utils';
import { useTranslation } from 'react-i18next';
import { namespace } from '@common-config/constants';
import { useAppDispatch, useAppSelector } from '@common-reducers/hooks/store.hook';
import { selectKeepWhatsappAwake } from '@common-reducers/UserSelectors';
import { setKeepWhatsappAwake } from '@common-reducers/UserThunks';
import hs from '@common/components/header-status/header-status.module.scss';
import { usePingStatus } from './use-ping-status';

const LEARN_MORE_PATH = '/keep-whatsapp-awake';


const KeepAwakeControl: React.FC = () => {
  const { t } = useTranslation(namespace);
  const dispatch = useAppDispatch();
  const enabled = useAppSelector(selectKeepWhatsappAwake);

  const { hasExtension, status } = usePingStatus();
  const [countdown, setCountdown] = useState<number>(4);

  // The raw `countdown|total` readout is a debug-only affordance. Read it once
  // (localStorage doesn't change mid-session) so the per-100ms ticker below can
  // be skipped entirely for normal users, who only ever see "Awake"/"Off".
  const [isDebug] = useState(
    () => typeof localStorage !== 'undefined' && localStorage.getItem('bt-debug') === 'true'
  );


  // Make sure the background's live state matches the saved preference once we
  // know the extension is there (covers SW restarts + cross-client MQTT flips).
  useEffect(() => {
    if (hasExtension) {
      window.postMessage({ type: 'SET_KEEP_AWAKE', enabled }, '*');
    }
  }, [hasExtension, enabled]);

  // Countdown ticker — only runs in debug mode (the only place the number shows).
  useEffect(() => {
    if (!isDebug || !status?.lastPingTime) return;
    const tick = () => {
      const elapsed = Date.now() - status.lastPingTime!;
      const budget = status.status === 'pending' ? status.timeout : status.interval;
      setCountdown(Math.ceil(Math.max(0, budget - elapsed) / 1000));
    };
    tick();
    const timer = setInterval(tick, 100);
    return () => clearInterval(timer);
  }, [isDebug, status?.lastPingTime, status?.interval, status?.timeout, status?.status]);

  const openLearnMore = () => window.open(LEARN_MORE_PATH, '_blank', 'noopener');

  // Always rendered — it's a fixed part of the header. Health state (countdown /
  // throttle colors) only shows when a live ping status exists; otherwise the
  // pill just reflects the toggle (Awake / Off).
  const isPending = enabled && status?.status === 'pending';
  const isTimeout = enabled && status?.status === 'timeout';
  const total = status ? (isPending ? status.timeout : status.interval) / 1000 : 4;

  // Tone + icon reflect: off (neutral) → throttle (bad) → pinging (warn) → ok.
  // The tone comes from the shared header-row vocabulary so this chip matches
  // the plan / bot / engine chips beside it; the four states stay distinct.
  let toneClass: string;
  let StatusIcon: typeof Zap;
  if (!enabled) {
    toneClass = hs.toneNeutral;
    StatusIcon = ZapOff;
  } else if (isTimeout) {
    toneClass = hs.toneBad;
    StatusIcon = AlertTriangle;
  } else if (isPending) {
    toneClass = hs.toneWarn;
    StatusIcon = Zap;
  } else {
    toneClass = hs.toneOk;
    StatusIcon = CheckCircle;
  }

  // Label: "Off" when disabled; the technical countdown in debug mode (only when
  // a live ping status is flowing); otherwise the friendly "Awake". Translated —
  // this pill sits in the WhatsApp top bar, so an English word here stood out in
  // an otherwise fully Hebrew/Arabic row. The debug countdown stays untranslated
  // on purpose: it is a raw diagnostic, not user-facing copy.
  const label = !enabled
    ? t('status.keepAwakeOff')
    : isDebug && status?.lastPingTime
      ? `${countdown}|${total}`
      : t('status.keepAwakeOn');

  // One plain explanation per state, on hover — no click needed. It used to be
  // a hard-coded English one-liner on the label plus a click-to-open popover
  // behind the "?", both untranslated.
  const hintBody = !enabled
    ? t('status.hint.keepAwake.off', "Off — we'll never switch your tabs. If WhatsApp sits in a background tab, Chrome may put it to sleep and scheduled messages can be delayed until you open it.")
    : isTimeout
      ? t('status.hint.keepAwake.notResponding', "Your WhatsApp tab isn't responding. Open it for a moment to wake it up.")
      : isPending
        ? t('status.hint.keepAwake.waking', 'Waking your WhatsApp tab right now.')
        : t('status.hint.keepAwake.on', 'On — when Chrome puts your WhatsApp tab to sleep, we briefly switch to it for under a second and then back to your tab, so scheduled messages keep sending.');
  const hintTone = !enabled ? 'neutral' : isTimeout ? 'bad' : isPending ? 'warn' : 'ok';
  const hintTitle = t('status.hint.keepAwake.title', 'Keep WhatsApp awake');

  const hint = (trigger: React.ReactElement) => (
    <HeaderHint
      tone={hintTone}
      title={hintTitle}
      body={<>{hintBody}{enabled && <> {t('status.hint.keepAwake.about', "Seeing your screen jump to WhatsApp? That's this setting. Turn it off, or use a Gateway to send without any browser tab.")}</>}</>}
      footer={
        <button type="button" onClick={openLearnMore} className={hs.hintLink}>
          {t('status.hint.learnMore', 'Learn more')}
          <ExternalLink size={11} />
        </button>
      }
    >
      {trigger}
    </HeaderHint>
  );

  return (
    <div className={cn(hs.chip, toneClass)}>
      {/* Status (icon + label/countdown) — hover for what this state means. */}
      {hint(
        <span className="inline-flex items-center gap-1.5">
          <StatusIcon className={hs.icon} size={14} />
          {/* tabular-nums + fixed min-width so the ticking countdown
              (4|4 → 3|4 …) doesn't shift the chip width each second. */}
          <span className={hs.label} style={{ minWidth: '2.5ch', textAlign: 'center', fontVariantNumeric: 'tabular-nums' }}>
            {label}
          </span>
        </span>
      )}

      {/* A hairline says "the toggle and the ? are a different thing from the
          status" without adding a second bordered box. */}
      <span className={hs.sep} />

      {/* The toggle — one click from the bar. Flattened (.softSwitch) to the
          row's borderless language. */}
      <Switch
        checked={enabled}
        onCheckedChange={(checked) => dispatch(setKeepWhatsappAwake(checked))}
        aria-label={hintTitle}
        className={cn('h-4 w-7', hs.softSwitch)}
      />

      {/* "?" — the same hint, on hover or keyboard focus. A bare glyph, no circle. */}
      {hint(
        <span tabIndex={0} role="img" aria-label={hintTitle} className={hs.helpMark}>?</span>
      )}
    </div>
  );
};

export default KeepAwakeControl;
